import { useState, useEffect, useRef } from 'react';
import { PRERENDER } from '../lib/prerender';

// ─── useFadeIn ────────────────────────────────────────────────────────────────
// Returns [ref, visible]. Once the element enters the viewport it stays visible
// (observer disconnects immediately — no flicker on scroll-back).
export const useFadeIn = (threshold = 0.15) => {
  const ref = useRef(null);
  // Start visible under prerender so the dumped HTML shows opacity:1 content.
  const [visible, setVisible] = useState(PRERENDER);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Already visible (e.g. fast scroll / cached) — skip
    if (visible) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect(); // fire once, then stop watching
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold, visible]);

  return [ref, visible];
};

// ─── FadeUp ───────────────────────────────────────────────────────────────────
export const FadeUp = ({ children, delay = 0, threshold = 0.12 }) => {
  const [ref, visible] = useFadeIn(threshold);
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(24px)',
      transition: `opacity 0.7s ease ${delay}s, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
    }}>
      {children}
    </div>
  );
};

// ─── CountUp ──────────────────────────────────────────────────────────────────
export const CountUp = ({ end, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [ref, visible] = useFadeIn(0.2);
  const started = useRef(false);

  useEffect(() => {
    if (!visible || started.current) return;
    started.current = true;
    const startTime = performance.now();
    const tick = (now) => {
      const elapsed  = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased    = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [visible, end, duration]);

  return <span ref={ref} style={{ display: 'inline' }}>{count}{suffix}</span>;
};

// ─── LazySection ─────────────────────────────────────────────────────────────
// Renders nothing until the section is within `rootMargin` of the viewport,
// then mounts the children and never unmounts them again.
// Use for heavy off-screen sections (review tickers, orbit animations, etc.)
// to prevent them from blocking the initial page render.
export const LazySection = ({ children, rootMargin = '400px 0px', style, className }) => {
  const ref  = useRef(null);
  // Mount immediately under prerender so below-fold sections aren't missing
  // from the dump (they'd otherwise stay null until the observer fires).
  const [mounted, setMounted] = useState(PRERENDER);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setMounted(true);
          obs.disconnect();
        }
      },
      { rootMargin }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [rootMargin]);

  return (
    <div ref={ref} style={style} className={className}>
      {mounted ? children : null}
    </div>
  );
};
